import { useState, useEffect } from "react";
import { Bell, Send, Users, UserCog, Clock, CheckCircle, Search, Check } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { getNotifications, sendNotification, getAllUsers } from "@/services/api";

export const Notifications = () => {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [recipient, setRecipient] = useState("all");
  const [type, setType] = useState("system");
  const [targetUserId, setTargetUserId] = useState("");
  const [selectedPersonName, setSelectedPersonName] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [tempSelectedId, setTempSelectedId] = useState("");
  const [tempSelectedName, setTempSelectedName] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [users, setUsers] = useState([]);
  const [captains, setCaptains] = useState([]);
  const [sentNotifications, setSentNotifications] = useState([]);
  const [scheduledNotifications, setScheduledNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const data = await getNotifications();
        if (data && data.success) {
          const notifications = data.notifications;
          setSentNotifications(notifications.filter(n => n.status === "delivered"));
          setScheduledNotifications(notifications.filter(n => n.status === "scheduled"));
        }
      } catch (error) {
        console.error("Error fetching notifications:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchNotifications();

    const fetchUsersAndAgents = async () => {
      try {
        // Fetch up to 1000 users/captains to match the Users page and ensure all targets are available
        const [usersData, captainsData] = await Promise.all([
          getAllUsers('user'),
          getAllUsers('agent')
        ]);

        if (usersData?.success) {
          const normalizedUsers = usersData.users.map(u => ({
            ...u,
            id: u.id || u._id,
            displayName: u.name || "Unknown User",
            displayContact: u.phone || u.contact || "No phone"
          }));
          setUsers(normalizedUsers);
        }
        
        if (captainsData?.success) {
          const normalizedCaptains = captainsData.users.map(a => ({
            ...a,
            id: a.id || a._id,
            displayName: a.name || "Unknown Captain",
            displayContact: a.contact || a.phone || "No contact"
          }));
          setCaptains(normalizedCaptains);
        }
      } catch (error) {
        console.error("Error fetching users/captains:", error);
      }
    };
    fetchUsersAndAgents();
  }, []);

  const handleSendNotification = async () => {
    if (!title || !message) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      let targetRole = recipient;
      let finalTargetUserId = targetUserId;

      if (targetRole === 'single' && !finalTargetUserId) {
        toast.error("Please select a specific user");
        return;
      }

      await sendNotification({
          title,
          body: message,
          targetRole,
          targetUserId: targetRole === 'single' ? finalTargetUserId : null,
          type
      });

      toast.success("Notification sent successfully!");
      setTitle("");
      setMessage("");
      setRecipient("all");
      setTargetUserId("");
      setSelectedPersonName("");
      setTempSelectedId("");
      setTempSelectedName("");
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error("Failed to send notification: " + (error.response?.data?.detail || error.message));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Notification Center</h1>
        <p className="text-muted-foreground mt-1">Broadcast messages and manage automated reminders</p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Sent</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">{sentNotifications.length}</h3>
              </div>
              <Send className="w-8 h-8 text-primary" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Scheduled</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">{scheduledNotifications.length}</h3>
              </div>
              <Clock className="w-8 h-8 text-secondary" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Delivered Rate</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">0%</h3>
              </div>
              <CheckCircle className="w-8 h-8 text-success" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Open Rate</p>
                <h3 className="text-2xl font-bold text-foreground mt-1">0%</h3>
              </div>
              <Bell className="w-8 h-8 text-accent" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Send Notification Form */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-primary" />
              Send New Notification
            </CardTitle>
            <CardDescription>Broadcast a message to users or captains</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="Enter notification title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="message">Message</Label>
              <Textarea
                id="message"
                placeholder="Enter notification message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="type">Notification Type</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger id="type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="system">System</SelectItem>
                    <SelectItem value="pickup">Pickup</SelectItem>
                    <SelectItem value="wallet">Wallet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="recipient">Send To</Label>
                <Select value={recipient} onValueChange={setRecipient}>
                  <SelectTrigger id="recipient">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All (Users & Captains)</SelectItem>
                    <SelectItem value="user">All Users</SelectItem>
                    <SelectItem value="agent">All Captains</SelectItem>
                    <SelectItem value="single">Single Target</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {recipient === "single" && (
              <div className="space-y-2">
                <Label>Target Person</Label>
                <div className="flex gap-2">
                  <Input 
                    placeholder="No person selected" 
                    value={selectedPersonName} 
                    readOnly 
                    className="bg-muted"
                  />
                  <Button variant="outline" onClick={() => setIsDialogOpen(true)}>
                    Select
                  </Button>
                </div>
              </div>
            )}

            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogContent className="sm:max-w-[500px] h-[600px] flex flex-col">
                <DialogHeader>
                  <DialogTitle>Select Recipient</DialogTitle>
                  <DialogDescription>
                    Choose a single user or captain to receive this notification.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="relative mt-2">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or contact..."
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>

                <Tabs defaultValue="users" className="flex-1 mt-4 flex flex-col overflow-hidden">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="users">Users</TabsTrigger>
                    <TabsTrigger value="agents">Captains</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="users" className="flex-1 overflow-hidden mt-2">
                    <ScrollArea className="h-full pr-4">
                      <div className="space-y-2">
                        {users
                          .filter(u => {
                            const query = searchQuery.toLowerCase();
                            return (u.name || "").toLowerCase().includes(query) || 
                                   (u.phone || "").includes(query) ||
                                   (u.contact || "").includes(query) ||
                                   (u.email || "").toLowerCase().includes(query);
                          })
                          .map((u) => (
                            <div
                              key={u.id}
                              onClick={() => {
                                setTempSelectedId(u.id);
                                setTempSelectedName(u.displayName);
                              }}
                              className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                                tempSelectedId === u.id 
                                  ? "bg-primary/10 border-primary" 
                                  : "hover:bg-muted"
                              }`}
                            >
                              <div>
                                <p className="font-medium">{u.displayName}</p>
                                <p className="text-sm text-muted-foreground">{u.displayContact}</p>
                                {u.email && <p className="text-[10px] text-muted-foreground italic">{u.email}</p>}
                              </div>
                              {tempSelectedId === u.id && <Check className="h-4 w-4 text-primary" />}
                            </div>
                          ))}
                      </div>
                    </ScrollArea>
                  </TabsContent>

                  <TabsContent value="agents" className="flex-1 overflow-hidden mt-2">
                    <ScrollArea className="h-full pr-4">
                      <div className="space-y-2">
                        {captains
                          .filter(a => {
                            const query = searchQuery.toLowerCase();
                            return (a.name || "").toLowerCase().includes(query) || 
                                   (a.contact || "").includes(query) ||
                                   (a.phone || "").includes(query) ||
                                   (a.email || "").toLowerCase().includes(query);
                          })
                          .map((a) => (
                            <div
                              key={a.id}
                              onClick={() => {
                                setTempSelectedId(a.id);
                                setTempSelectedName(a.displayName);
                              }}
                              className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                                tempSelectedId === a.id 
                                  ? "bg-primary/10 border-primary" 
                                  : "hover:bg-muted"
                              }`}
                            >
                              <div>
                                <p className="font-medium">{a.displayName}</p>
                                <p className="text-sm text-muted-foreground">{a.displayContact}</p>
                                {a.email && <p className="text-[10px] text-muted-foreground italic">{a.email}</p>}
                              </div>
                              {tempSelectedId === a.id && <Check className="h-4 w-4 text-primary" />}
                            </div>
                          ))}
                      </div>
                    </ScrollArea>
                  </TabsContent>
                </Tabs>

                <DialogFooter className="mt-4 gap-2">
                  <Button variant="ghost" onClick={() => {
                    setIsDialogOpen(false);
                    setTempSelectedId("");
                    setTempSelectedName("");
                  }}>
                    Cancel
                  </Button>
                  <Button 
                    disabled={!tempSelectedId}
                    onClick={() => {
                      setTargetUserId(tempSelectedId);
                      setSelectedPersonName(tempSelectedName);
                      setIsDialogOpen(false);
                    }}
                  >
                    Select
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button
              onClick={handleSendNotification}
              className="w-full bg-primary hover:bg-primary-dark text-primary-foreground"
            >
              <Send className="w-4 h-4 mr-2" />
              Send Notification
            </Button>
          </CardContent>
        </Card>

        {/* Automated Reminders */}
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Automated Reminders
            </CardTitle>
            <CardDescription>Configure automatic notification triggers</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="p-4 border rounded-lg hover:bg-muted/50 transition-smooth">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h4 className="font-medium text-foreground">Subscription Renewal</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Remind users 3 days before subscription expires
                  </p>
                </div>
                <Badge className="bg-success text-success-foreground">Active</Badge>
              </div>
            </div>
            <div className="p-4 border rounded-lg hover:bg-muted/50 transition-smooth">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h4 className="font-medium text-foreground">Pickup Reminder</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Remind users 1 day before scheduled pickup
                  </p>
                </div>
                <Badge className="bg-success text-success-foreground">Active</Badge>
              </div>
            </div>
            <div className="p-4 border rounded-lg hover:bg-muted/50 transition-smooth">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <h4 className="font-medium text-foreground">Inactive User</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Re-engage users inactive for 30 days
                  </p>
                </div>
                <Badge className="bg-muted text-muted-foreground">Inactive</Badge>
              </div>
            </div>
            <Button variant="outline" className="w-full mt-4">
              Configure Reminders
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Notification History */}
      <Card className="border-border">
        <CardHeader>
          <CardTitle>Notification History</CardTitle>
          <CardDescription>View sent and scheduled notifications</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="sent">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="sent">Sent Notifications</TabsTrigger>
              <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
            </TabsList>
            <TabsContent value="sent" className="space-y-3 mt-4">
              {sentNotifications.map((notification) => (
                <div key={notification.id} className="p-4 border rounded-lg hover:bg-muted/50 transition-smooth">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="font-semibold text-foreground">{notification.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{notification.message}</p>
                    </div>
                    <Badge className="ml-4">{notification.status}</Badge>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>{notification.recipient}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>{new Date(notification.date).toLocaleDateString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              ))}
            </TabsContent>
            <TabsContent value="scheduled" className="space-y-3 mt-4">
              {scheduledNotifications.map((notification) => (
                <div key={notification.id} className="p-4 border rounded-lg hover:bg-muted/50 transition-smooth">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="font-semibold text-foreground">{notification.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{notification.message}</p>
                    </div>
                    <Badge className="ml-4 bg-warning text-warning-foreground">{notification.status}</Badge>
                  </div>
                  <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      <span>{notification.recipient}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      <span>Scheduled: {new Date(notification.scheduledDate).toLocaleDateString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              ))}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Notifications;
